import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import { zipSync, Zippable } from 'fflate';
import {
  SituRenderServiceWebApiClient, GetSituFacadeDownloadLinksRequest, GetSituFacadeDownloadLinksResponse, SituFacadeDownloadLink
} from 'siturenderservice-webapi-client';

const FETCH_CONCURRENCY = 4;

/**
 * Facade downloads come straight from blob storage via short-lived SAS links,
 * so the render service only signs links and never streams the files itself.
 * A single facade is a plain browser download; several are zipped client-side.
 */
@Injectable({
  providedIn: 'root'
})
export class FacadeDownloadService {
  constructor(
    private readonly renderServiceClient: SituRenderServiceWebApiClient
  ) {}

  /**
   * Resolves with the number of files downloaded, or rejects if none could be.
   * @param zipName full name for the zip; defaults to the facades-yyyyMMddhhmmss.zip the render service used to give
   */
  async download(companyId: string | undefined, facadeIds: string[], zipName?: string): Promise<number> {
    const links = await this.getLinks(companyId, facadeIds);

    if (links.length === 1) {
      // The SAS carries Content-Disposition: attachment, so navigating saves the file without a CORS fetch
      this.navigateToDownload(links[0].url!);
      return 1;
    }

    const files = await this.fetchAll(links);
    const count = Object.keys(files).length;
    if (!count) {
      throw new Error('None of the facades could be downloaded.');
    }

    // JPGs are already compressed; store them as-is rather than burn CPU deflating
    const zipped = zipSync(files, { level: 0 });
    FileSaver.saveAs(new Blob([zipped], { type: 'application/zip' }), zipName ?? `facades-${this.timestamp()}.zip`);
    return count;
  }

  private async getLinks(companyId: string | undefined, facadeIds: string[]): Promise<SituFacadeDownloadLink[]> {
    const request = new GetSituFacadeDownloadLinksRequest();
    request.companyId = companyId;
    request.facadeIds = facadeIds;

    const response: GetSituFacadeDownloadLinksResponse = await this.renderServiceClient.getSituFacadeDownloadLinks(request);
    if (!response?.success || !response.links?.length) {
      throw new Error(response?.errorMessages?.join(', ') || 'No facades available to download.');
    }

    return response.links;
  }

  private async fetchAll(links: SituFacadeDownloadLink[]): Promise<Zippable> {
    const files: Zippable = {};
    const queue = [...links];

    const worker = async () => {
      for (let link = queue.shift(); link; link = queue.shift()) {
        try {
          const response = await fetch(link.url!);
          if (!response.ok) {
            console.warn(`Facade ${link.facadeId} download failed: ${response.status}`);
            continue;
          }
          files[link.fileName!] = new Uint8Array(await response.arrayBuffer());
        } catch (err) {
          console.warn(`Facade ${link.facadeId} download failed`, err);
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(FETCH_CONCURRENCY, links.length) }, worker));
    return files;
  }

  private navigateToDownload(url: string) {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }

  // Matches the name the render service gave its zip: yyyyMMddhhmmss (12-hour hh)
  private timestamp(): string {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours() % 12 || 12)}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  }
}
